import React, { FC } from 'react';
import { useSelector } from 'react-redux';
import { selectOrderImageIntentGallery } from '../../../../selectors/imageIntent';
import {
  ImageIntentGallery,
  imageIntentGalleryOf,
} from '../../shared/protectedImage/imageIntentGallery';
import useImageIntentFriction from '../../shared/protectedImage/useImageIntentFriction';
import styled from 'styled-components';
import UntypedPoseItem from '../../shared/poseItem/PoseItem';
import { levelForPhoto, RetouchingSelections } from './retouchingSelections';
import RetouchingPhotoNote from './RetouchingPhotoNote';
import { RetouchingPose, RetouchingProduct } from './types';

// PoseItem is untyped JSX; TypeScript reads every destructured prop as
// required, including the optional image-intent gallery.
const PoseItem = UntypedPoseItem as unknown as FC<{
  name: string;
  intentGallery?: Partial<ImageIntentGallery>;
  intentSurface?: string;
  registerIntentSaveShortcut?: boolean;
  selected?: boolean;
  src?: string;
  onClick: () => void;
}>;

const ACCENT = '#3085d6';

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(190px, 1fr));
  gap: 20px;

  @media (max-width: 767px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 12px;
  }
`;

const Tile = styled.div<{ $selected: boolean }>`
  position: relative;
  display: flex;
  flex-direction: column;
  padding: 8px;
  background: #fff;
  border: 2px solid ${(props) => (props.$selected ? ACCENT : '#e3e3e3')};
  border-radius: 8px;
`;

// PoseItem sizes itself for a four-across flex row; here the grid cell sets
// the width, so its root (this wrapper's only child) fills the tile's width
// whatever else the tile holds. It must not grow in height: tiles in a row
// stretch to the tallest, and the level choices should sit right under each
// photo, not wherever the stretch leaves them. PoseItem's root is a plain
// div we cannot style directly, hence the child selector.
const Photo = styled.div`
  flex: none;

  > div {
    flex: none;
    max-width: 100%;
  }
`;

// The photo's corner badges sit top left; the zoom control holds top right.
const Badges = styled.div`
  position: absolute;
  top: 14px;
  left: 14px;
  z-index: 2;
  display: flex;
  gap: 6px;
  pointer-events: none;
`;

const LevelBadge = styled.span`
  padding: 2px 10px;
  border-radius: 12px;
  background: ${ACCENT};
  color: #fff;
  font-size: 13px;
  line-height: 20px;
`;

// A photo added since the order's last retouching offer.
const NewBadge = styled.span`
  padding: 2px 10px;
  border-radius: 12px;
  background: #fff;
  border: 1px solid ${ACCENT};
  color: ${ACCENT};
  font-size: 13px;
  font-weight: 700;
  line-height: 18px;
`;

const LevelChoices = styled.fieldset`
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin: 10px 0 0;
  padding: 0;
  border: 0;
  min-width: 0;
`;

const VisuallyHidden = styled.legend`
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip: rect(0 0 0 0);
  white-space: nowrap;
`;

const LevelChoice = styled.label<{ $checked: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  min-height: 40px;
  margin: 0;
  padding: 6px 10px;
  border: 1px solid ${(props) => (props.$checked ? ACCENT : '#d9d9d9')};
  border-radius: 6px;
  background: ${(props) => (props.$checked ? '#eef5fc' : '#fff')};
  font-size: 14px;
  font-weight: ${(props) => (props.$checked ? 600 : 400)};
  line-height: 1.3;
  cursor: pointer;
`;

const LevelRadio = styled.input`
  flex: none;
  margin: 0;
  accent-color: ${ACCENT};
`;

interface RetouchingPhotoPickerProps {
  orderKey: string | null;
  poses: RetouchingPose[];
  levels: RetouchingProduct[];
  selections: RetouchingSelections;
  // Photos added since the order's last retouching offer, badged New.
  newPoseIds?: number[];
  onTogglePhoto: (poseId: number) => void;
  onChooseLevel: (poseId: number, productId: number) => void;
  notes: Record<number, string>;
  notesMissing: number[];
  onNoteChange: (poseId: number, note: string) => void;
}

// One tile per photo. Tapping the photo adds or removes it; when the studio
// sells more than one level, each photo also gets its own level choice, so a
// photo can never be bought at two levels at once.
const RetouchingPhotoPicker: FC<RetouchingPhotoPickerProps> = ({
  orderKey,
  poses,
  levels,
  selections,
  newPoseIds = [],
  onTogglePhoto,
  onChooseLevel,
  notes,
  notesMissing,
  onNoteChange,
}) => {
  const intentGallery = useSelector(
    (state: Parameters<typeof selectOrderImageIntentGallery>[0]) =>
      selectOrderImageIntentGallery(state, orderKey),
  );
  // One page-save context for the edited order, independent of pose order.
  useImageIntentFriction({ ...intentGallery, surface: 'retouching' });
  const levelIds = levels.map((level) => level.id);
  const multipleLevels = levels.length > 1;

  return (
    <Grid>
      {poses.map((pose, index) => {
        const name = `Photo ${index + 1}`;
        const levelId = levelForPhoto(selections, levelIds, pose.id);
        const level = levels.find((candidate) => candidate.id === levelId);
        const showsLevelCheck = Boolean(level) && !multipleLevels;
        const isNew = newPoseIds.includes(pose.id);

        return (
          <Tile
            key={pose.id}
            $selected={Boolean(level)}
            data-testid={`retouching-photo-${pose.id}`}
            data-selected={Boolean(level)}
          >
            <Photo>
              <PoseItem
                name={name}
                intentGallery={{
                  ...intentGallery,
                  ...imageIntentGalleryOf(pose),
                }}
                intentSurface="retouching"
                registerIntentSaveShortcut={false}
                selected={Boolean(level)}
                // Retouching is applied to the original, never a print's crop.
                src={pose.url}
                onClick={() => onTogglePhoto(pose.id)}
              />
            </Photo>
            {(showsLevelCheck || isNew) && (
              <Badges>
                {/* With several levels the checked choice below names the
                    level. */}
                {showsLevelCheck && <LevelBadge>✓</LevelBadge>}
                {isNew && <NewBadge>New</NewBadge>}
              </Badges>
            )}
            {multipleLevels && (
              <LevelChoices>
                <VisuallyHidden>Retouching level for {name}</VisuallyHidden>
                {levels.map((candidate) => (
                  <LevelChoice
                    key={candidate.id}
                    $checked={candidate.id === levelId}
                  >
                    <LevelRadio
                      type="radio"
                      name={`retouching-level-${pose.id}`}
                      checked={candidate.id === levelId}
                      onChange={() => onChooseLevel(pose.id, candidate.id)}
                    />
                    {candidate.name}
                  </LevelChoice>
                ))}
              </LevelChoices>
            )}
            {level &&
              (level.retouching_note_mode === 'optional' ||
                level.retouching_note_mode === 'required') && (
                <RetouchingPhotoNote
                  poseId={pose.id}
                  photoName={name}
                  mode={level.retouching_note_mode}
                  value={notes[pose.id] || ''}
                  invalid={notesMissing.includes(pose.id)}
                  onChange={(note) => onNoteChange(pose.id, note)}
                />
              )}
          </Tile>
        );
      })}
    </Grid>
  );
};

export default RetouchingPhotoPicker;
